import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, tap } from 'rxjs';

import { AppService } from '../../../app.service';
import { environment } from '../../../../environments/environment';
import { buildGtwAuthHeaders } from '../../../core/http/gtw-auth-headers.util';

export interface S3FileUploadRequest {
  fileKey?: string;
  path: string;
  rootFolder: string;
  clientKey?: string;
}

export interface S3FileUploadInput extends S3FileUploadRequest {
  baseUrl: string;
  readOnlyS3Paths: string;
  canEditPath: boolean;
  onlyCore: boolean;
  hidePath: boolean;
  title?: string;
}

@Injectable({
  providedIn: 'root',
})
export class S3FileUploadService {
  /**
   * Parents that embed `<gtw-s3-file-upload>` (no `row-data` host attribute)
   * publish a partial config here before or while the child mounts — same
   * contract as the legacy `s3FileUploadInput` @Input.
   */
  private readonly uploadViewContext$ = new BehaviorSubject<
    Partial<S3FileUploadInput> | null
  >(null);

  constructor(
    private httpClient: HttpClient,
    private appService: AppService
  ) {}

  /** Publish or clear the next resolved input for embedded upload views. */
  publishUploadViewContext(ctx: Partial<S3FileUploadInput> | null): void {
    this.uploadViewContext$.next(ctx);
  }

  getUploadViewContextStream(): Observable<Partial<S3FileUploadInput> | null> {
    return this.uploadViewContext$.asObservable();
  }

  /**
   * Builds the same partial input as `ui/.../s3ManagementCtrl.js`
   * (`s3ManagementUploadCtrl` + `vm.s3FileUploadInput`), using AppService for
   * user/server defaults and optional admin base URL from the CE host.
   */
  buildPartialFromS3ManagementRowData(
    rowData: Record<string, unknown> | null | undefined,
    baseUrlFromHost?: string | null
  ): Partial<S3FileUploadInput> {
    const defaultClientKey = this.resolveDefaultClientKey();
    const cfg = this.appService.SERVER_CONFIG?.configSettings || {};
    const readOnlyS3Paths =
      typeof cfg.read_only_s3_paths === 'string' &&
      cfg.read_only_s3_paths.length > 0
        ? cfg.read_only_s3_paths
        : '/common/tax-forms/pdf,/gtw/binaries';

    const baseUrl =
      baseUrlFromHost != null && String(baseUrlFromHost).trim() !== ''
        ? String(baseUrlFromHost).trim()
        : environment.admin_api_url;

    if (!rowData || Object.keys(rowData).length === 0) {
      return {
        baseUrl,
        clientKey: defaultClientKey,
        path: '',
        readOnlyS3Paths,
        canEditPath: true,
        onlyCore: false,
      };
    }

    const onlyCore =
      rowData.S3_CLIENT_KEY === null || rowData.S3_CLIENT_KEY === undefined;

    const clientKey = onlyCore
      ? ''
      : rowData.S3_CLIENT_KEY != null
        ? String(rowData.S3_CLIENT_KEY)
        : defaultClientKey;

    return {
      fileKey: rowData.FILE_KEY as string | undefined,
      baseUrl,
      path:
        typeof rowData.FILE_PATH === 'string' ? rowData.FILE_PATH : '',
      readOnlyS3Paths,
      canEditPath: true,
      onlyCore,
      clientKey,
    };
  }

  /**
   * Merges host-provided JSON/object with AppService settings (admin URL,
   * read-only S3 paths from SERVER_CONFIG, default client key from USER_SETTINGS)
   * so inputs only need row- or screen-specific overrides.
   */
  resolveS3FileUploadInput(
    input: Partial<S3FileUploadInput> | S3FileUploadInput
  ): S3FileUploadInput {
    const cfg = this.appService.SERVER_CONFIG?.configSettings || {};
    const defaultClientKey = this.resolveDefaultClientKey();
    const adminBase = environment.admin_api_url || '/admin';
    const readOnlyFromServer =
      typeof cfg.read_only_s3_paths === 'string' &&
      cfg.read_only_s3_paths.length
        ? cfg.read_only_s3_paths
        : '/common/tax-forms/pdf,/gtw/binaries';

    const out: S3FileUploadInput = {
      baseUrl: adminBase,
      clientKey: defaultClientKey,
      path: '/preformatteddisclosure/',
      rootFolder: 'gtw',
      readOnlyS3Paths: readOnlyFromServer,
      /** Default true so S3 management / new-upload modals show folder + Add until a partial overrides (e.g. embedded folder manager sets false). */
      canEditPath: true,
      onlyCore: false,
      hidePath: false,
      title: undefined,
      fileKey: input.fileKey,
    };

    if (input.baseUrl != null && String(input.baseUrl).trim() !== '') {
      out.baseUrl = String(input.baseUrl);
    }
    if (input.clientKey !== undefined && input.clientKey !== null) {
      out.clientKey = String(input.clientKey);
    }
    if (input.path !== undefined && input.path !== null) {
      out.path = String(input.path);
    }
    if (
      input.rootFolder !== undefined &&
      input.rootFolder !== null &&
      String(input.rootFolder).trim() !== ''
    ) {
      out.rootFolder = String(input.rootFolder);
    }
    if (
      input.readOnlyS3Paths !== undefined &&
      input.readOnlyS3Paths !== null &&
      String(input.readOnlyS3Paths).trim() !== ''
    ) {
      out.readOnlyS3Paths = String(input.readOnlyS3Paths);
    }
    if (input.canEditPath !== undefined) {
      out.canEditPath = !!input.canEditPath;
    }
    if (input.onlyCore !== undefined) {
      out.onlyCore = !!input.onlyCore;
    }
    if (input.hidePath !== undefined) {
      out.hidePath = !!input.hidePath;
    }
    if (input.title !== undefined) {
      out.title = input.title;
    }
    if (input.fileKey !== undefined) {
      out.fileKey = input.fileKey;
    }
    return out;
  }

  /**
   * POST multipart upload with explicit GTW auth headers (admin
   * `/uploadOrReplaceFileinS3` requires Authorization; ng2-file-upload XHR
   * does not use {@link AuthInterceptor}).
   */
  uploadFile(
    url: string,
    file: File,
    uploadParams: S3FileUploadRequest
  ): Observable<any> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('path', uploadParams.path ?? '');
    formData.append('fileKey', uploadParams.fileKey ?? '');
    formData.append('rootFolder', uploadParams.rootFolder ?? '');
    const clientKey = uploadParams.clientKey;
    if (
      clientKey !== undefined &&
      clientKey !== null &&
      String(clientKey).trim() !== ''
    ) {
      formData.append('clientKey', String(clientKey));
    }

    return this.httpClient.post<any>(url, formData, {
      headers: buildGtwAuthHeaders(),
      withCredentials: true,
    });
  }

  private resolveDefaultClientKey(): string {
    const user = this.appService.USER_SETTINGS?.user || {};
    const rawClientKey =
      user.client_key ??
      user.client?.client_key ??
      sessionStorage.getItem('client_key') ??
      '';

    return rawClientKey != null ? String(rawClientKey) : '';
  }

  downloadFile(
    fileKey: string | undefined,
    clientKey: string | undefined,
    baseUrl: string
  ): Observable<HttpResponse<Blob>> {
    let params = new HttpParams({
      fromObject: {
        fileKey: fileKey?.toString() || '',
        clientKey: clientKey?.toString() || '',
      },
    });

    if (!clientKey?.toString()) {
      params = params.delete('clientKey');
    }

    const url = `${baseUrl}/s3/download`;
    return this.httpClient
      .get(url, {
        headers: buildGtwAuthHeaders(),
        withCredentials: true,
        params,
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(
        tap((response) => {
          if (response.body) {
            const downloadLink = document.createElement('a');
            const blobUrl = window.URL.createObjectURL(response.body);
            downloadLink.href = blobUrl;
            const fileName = response.headers
              .get('content-disposition')
              ?.replace(/^attachment;\s*filename\s*=\s*/i, '')
              ?.replace(/^"|"$/g, '');
            if (fileName) {
              downloadLink.setAttribute('download', fileName);
            }
            downloadLink.target = '_blank';
            document.body.appendChild(downloadLink);
            downloadLink.click();
            downloadLink.remove();
            window.URL.revokeObjectURL(blobUrl);
          }
        })
      );
  }
}
